<template>
  <div class="musubi-tabulator" :class="{ 'is-row-clickable': clickable }">
    <div v-if="loading" class="musubi-tabulator__loading">Loading…</div>
    <div v-else-if="error" class="musubi-tabulator__error alert alert-warning">{{ error }}</div>
    <div v-else-if="!remote && !rows.length" class="musubi-tabulator__empty">No rows.</div>
    <div v-show="!loading && !error && (remote || rows.length)" ref="tableEl" class="musubi-tabulator__table" />
  </div>
</template>

<script>
/**
 * MusubiTabulator — thin Vue wrapper around tabulator-tables v6.3.1.
 * Lifted from Hermes Phase 5's HermesTabulator (~170 LOC). Same contract:
 *   - Pass :rows (already-fetched array) + :columns (lean Musubi shape).
 *   - Doesn't fetch — the host owns data loading.
 * Future migration: swap for `@apexl/icarus-tabulator` once that vendor
 * package exists; until then this gets us Tabulator-driven admin lists
 * with zero TC-local dependencies.
 */
import { getCurrentInstance, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import {
  AjaxModule, EditModule, FilterModule, FormatModule, InteractionModule,
  PageModule, ResizeColumnsModule, SelectRowModule, SortModule, Tabulator,
} from 'tabulator-tables';
import 'tabulator-tables/dist/css/tabulator.min.css';

Tabulator.registerModule([
  // EditModule is required for header-filter inputs to RENDER (they're built
  // from editor components) — without it `headerFilter` is silently a no-op.
  // AjaxModule powers the opt-in remote (server-side) pagination/sort/filter.
  AjaxModule, EditModule, FilterModule, FormatModule, InteractionModule,
  PageModule, ResizeColumnsModule, SelectRowModule, SortModule,
]);

export default {
  name: 'MusubiTabulator',
  props: {
    rows:        { type: Array,  default: () => [] },
    /**
     * Column defs:
     *   { key, label, format?, sorter?, width?, hozAlign?, headerFilter? }
     */
    columns:     { type: Array,  required: true },
    storageKey:  { type: String, default: '' },
    initialSort: { type: Object, default: () => ({}) },
    pageSize:    { type: Number, default: 25 },
    loading:     { type: Boolean, default: false },
    error:       { type: String,  default: '' },
    /** Opt-in checkbox row selection. Emits `selection-change` with row data. */
    selectable:  { type: Boolean, default: false },
    /**
     * Opt-in server-side mode. When true the table ignores `:rows` and drives
     * pagination/sort/filter remotely through `fetcher`. Static-rows callers are
     * unaffected (default false).
     */
    remote:      { type: Boolean, default: false },
    /**
     * Remote data source: async ({page, size, sort, filters}) => ({rows, total}).
     *   sort    — {field, dir} | null   (current sort)
     *   filters — [{field, type, value}] (active header filters)
     * Required when `remote` is true.
     */
    fetcher:     { type: Function, default: null },
  },
  emits: ['row-click', 'selection-change', 'filtered-count', 'params-change'],
  setup(props, { emit }) {
    const tableEl = ref(null);
    let tabulator = null;

    // Only show the clickable affordance (pointer + stronger hover) when the
    // host actually wired a @row-click listener. Declared emits are stripped
    // from $attrs, but the raw listener is still on the vnode's props. It's
    // fixed for a mounted instance, so a one-time boolean is enough.
    const inst = getCurrentInstance();
    const clickable = !!inst?.vnode?.props?.onRowClick;

    function buildColumnDef(c) {
      const def = {
        title: c.label,
        field: c.key,
        headerFilter: c.headerFilter ?? 'input',
        headerSort: c.sorter !== false,
        resizable: true,
      };
      if (c.width)    def.width = c.width;
      if (c.hozAlign) def.hozAlign = c.hozAlign;
      if (c.sorter)   def.sorter = c.sorter;
      // `layout: 'fitColumns'` (below) will squeeze columns to fill the
      // container, but with enough columns on screen (campaigns/lists/
      // subscribers/segments) it was squeezing headers below their own
      // label's width — "Recipients" ellipsizing to "RECIPIE…", "ID" down
      // to "I…". Give every column a floor it can never shrink past: ~8px
      // per header character (uppercase + letter-spaced) plus cell padding,
      // so a full label always fits. Host columns can still opt into a
      // larger explicit minWidth via `c.minWidth`.
      def.minWidth = c.minWidth ?? Math.max(c.width || 0, (c.label ? c.label.length : 3) * 8 + 40, 60);
      // Typed header filters (dropdowns / numeric ≥ / placeholders) — pass
      // through so columns can opt into list/number filters, not just text.
      if (c.headerFilterParams)      def.headerFilterParams = c.headerFilterParams;
      if (c.headerFilterFunc)        def.headerFilterFunc = c.headerFilterFunc;
      if (c.headerFilterPlaceholder) def.headerFilterPlaceholder = c.headerFilterPlaceholder;
      if (c.format) {
        def.formatter = (cell) => {
          const row = cell.getRow().getData();
          const raw = cell.getValue();
          return String(c.format(raw, row));
        };
      } else {
        def.formatter = (cell) => {
          const v = cell.getValue();
          if (v === null || v === undefined || v === '') return '<span class="text-muted">—</span>';
          return String(v).replace(/[<>&"]/g, ch => ({ '<':'&lt;','>':'&gt;','&':'&amp;','"':'&quot;' }[ch]));
        };
      }
      return def;
    }

    function build() {
      if (!tableEl.value) return;
      if (tabulator) {
        try { tabulator.destroy(); } catch (_) {}
        tabulator = null;
      }
      const cols = props.columns.map(buildColumnDef);
      if (props.selectable) {
        // Leading per-row checkbox. No built-in header select-all — its
        // behaviour vs active filters/pagination is ambiguous; the host drives
        // "select all matching the current filters" via selectAllActive().
        cols.unshift({
          formatter: 'rowSelection',
          hozAlign: 'center', headerSort: false, width: 42, frozen: false, resizable: false,
        });
      }
      const opts = {
        // In remote mode Tabulator must own data loading via ajax — passing a
        // `data` array (even empty) makes it a local source and skips the
        // initial fetch, so omit it.
        ...(props.remote ? {} : { data: props.rows }),
        columns: cols,
        // `fitColumns` works with the base module set we registered.
        // `fitDataStretch` + `responsiveLayout: 'collapse'` need
        // ResponsiveLayoutModule + FrozenColumnsModule (not registered).
        layout: 'fitColumns',
        height: 'auto',
        movableColumns: true,
        placeholder: 'No rows.',
      };
      if (props.selectable) {
        opts.selectableRows = true;
        // Persist ticks across page/sort/filter changes by row identity.
        if (props.remote) opts.index = 'id';
      }
      if (props.pageSize > 0) {
        opts.pagination = true;
        opts.paginationSize = props.pageSize;
        opts.paginationSizeSelector = [10, 25, 50, 100];
      }
      // Remote (server-side) mode: Tabulator manages page/sort/filter state and
      // calls our authed fetcher for each change. ajaxURL is a required-non-empty
      // placeholder — ajaxRequestFunc fully replaces the transport.
      if (props.remote && typeof props.fetcher === 'function') {
        opts.ajaxURL = 'remote';
        opts.paginationMode = 'remote';
        opts.sortMode = 'remote';
        opts.filterMode = 'remote';
        opts.ajaxRequestFunc = async (_url, _config, params) => {
          const sort = Array.isArray(params.sort) && params.sort[0]
            ? { field: params.sort[0].field, dir: params.sort[0].dir } : null;
          const filters = Array.isArray(params.filter) ? params.filter : [];
          emit('params-change', { sort, filters });
          let total = 0; let data = [];
          try {
            const res = await props.fetcher({ page: params.page || 1, size: params.size || props.pageSize, sort, filters });
            data = res?.rows || [];
            total = Number(res?.total) || 0;
          } catch (_) { data = []; total = 0; }
          emit('filtered-count', total);
          const size = params.size || props.pageSize || data.length || 1;
          return { data, last_page: Math.max(1, Math.ceil(total / size)) };
        };
      }
      if (props.initialSort?.column) {
        opts.initialSort = [{ column: props.initialSort.column, dir: props.initialSort.dir || 'desc' }];
      }
      if (props.storageKey) {
        opts.persistence = { sort: true, filter: true, columns: true, page: true };
        opts.persistenceID = `musubi-tab:${props.storageKey}`;
      }
      tabulator = new Tabulator(tableEl.value, opts);
      // Surface row clicks to the host (edit-on-click). Tabulator's own
      // rowClick callback is the only reliable hook — the wrapper used to
      // drop it, so every `@row-click` listener was silently dead.
      tabulator.on('rowClick', (e, row) => emit('row-click', row.getData()));
      if (props.selectable) {
        tabulator.on('rowSelectionChanged', (data) => emit('selection-change', data));
        // Surface how many rows currently pass the active header filters so the
        // host can offer a "select all N matching" action + clear stale picks.
        // Remote mode emits the true server-side total from the fetcher instead
        // (dataFiltered would only ever see the current page).
        if (!props.remote) {
          tabulator.on('dataFiltered', (filters, rows) => emit('filtered-count', rows.length));
        }
      }
    }

    // Let the host clear the selection (e.g. after a bulk action completes).
    function deselect() { try { tabulator?.deselectRow(); } catch (_) {} }
    // Select every row that passes the active header filters (NOT the hidden
    // ones), so a bulk action always obeys the filters on screen.
    function selectAllActive() { try { tabulator?.selectRow(tabulator.getRows('active')); } catch (_) {} }
    // Count of rows passing the active filters (full set when unfiltered).
    function activeCount() { try { return tabulator ? tabulator.getRows('active').length : 0; } catch (_) { return 0; } }
    // Remote mode: re-run the fetcher (e.g. after the host's top-bar filters
    // change). Resets to page 1.
    function reload() { try { tabulator?.setData(); } catch (_) {} }
    // Current header filters in Tabulator's [{field,type,value}] shape — the
    // host serialises these for the stats / select-all-ids requests so they
    // match the page query exactly.
    function columnFilters() { try { return tabulator ? tabulator.getFilters(true) : []; } catch (_) { return []; } }

    onMounted(build);
    onBeforeUnmount(() => {
      if (tabulator) { try { tabulator.destroy(); } catch (_) {} tabulator = null; }
    });

    watch(() => [props.rows, props.columns, props.storageKey], build, { deep: false });

    return { tableEl, clickable, deselect, selectAllActive, activeCount, reload, columnFilters };
  },
};
</script>

<style>
.musubi-tabulator { font-family: var(--m-font-body, system-ui); font-size: 0.85rem; }
.musubi-tabulator__loading,
.musubi-tabulator__empty { color: var(--m-text-tertiary, #94A3B8); padding: 24px; text-align: center; }
.musubi-tabulator .tabulator { border: 1px solid var(--m-border, #E2E8F0); border-radius: var(--m-radius, 6px); background: var(--m-bg-card, #fff); }
.musubi-tabulator .tabulator .tabulator-header { background: #F8FAFC; border-bottom: 1px solid var(--m-border, #E2E8F0); }
.musubi-tabulator .tabulator .tabulator-header .tabulator-col {
  background: transparent;
  border-right: 1px solid var(--m-border, #E2E8F0);
  color: var(--m-text-secondary, #64748B);
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.04em;
  font-weight: 600;
}
.musubi-tabulator .tabulator .tabulator-header .tabulator-col .tabulator-col-content { padding: 10px 12px; }
.musubi-tabulator .tabulator .tabulator-header .tabulator-col .tabulator-header-filter input {
  border: 1px solid var(--m-border, #E2E8F0);
  border-radius: var(--m-radius, 6px);
  padding: 4px 8px;
  font-size: 0.8rem;
  background: #fff;
  color: var(--m-text-primary, #0F172A);
}
.musubi-tabulator .tabulator .tabulator-row { border-bottom: 1px solid var(--m-border, #E2E8F0); }
.musubi-tabulator .tabulator .tabulator-row:hover { background: #F8FAFC; }
/* Vendor tabulator.min.css paints selected rows steel-blue (#9abcea) — reads as
   the dark palette against our light tables. Re-point at the indigo wash. */
.musubi-tabulator .tabulator .tabulator-row.tabulator-selected { background: var(--m-accent-soft, #EEF0FF); }
.musubi-tabulator .tabulator .tabulator-row.tabulator-selected:hover { background: #E2E5FD; }
.musubi-tabulator .tabulator .tabulator-row.tabulator-selected .tabulator-cell { color: var(--m-text-primary, #0F172A); }
.musubi-tabulator .tabulator .tabulator-row .tabulator-cell { padding: 10px 12px; border-right: 1px solid var(--m-border, #E2E8F0); color: var(--m-text-primary, #0F172A); }
.musubi-tabulator .tabulator .tabulator-footer { background: #F8FAFC; border-top: 1px solid var(--m-border, #E2E8F0); padding: 4px 8px; }
.musubi-tabulator .tabulator .tabulator-paginator label,
.musubi-tabulator .tabulator .tabulator-paginator select,
.musubi-tabulator .tabulator .tabulator-page { font-size: 0.78rem; }
.musubi-tabulator .text-muted { color: var(--m-text-tertiary, #94A3B8); }
</style>
